/* tabularis.cl/drive: la ventana que trae archivos de Google Drive.
 *
 * La abre el panel de un estudio (utils/drive.ts → elegirDeDrive). Aquí se
 * inicia sesión con Google, el abogado elige archivos con el selector de
 * Google, se descargan, y se le entregan a la pestaña que abrió la ventana.
 *
 * Carpetas, no: con drive.file Google da acceso a lo que se marca, y una
 * carpeta marcada llega sin su contenido (probado el 9-oct-2026). Para una
 * carpeta entera, el camino es descargarla de Drive como .zip y arrastrarla a
 * Importar documentos, que lee los .zip directamente. Es una página aparte porque Google no deja
 * autorizar los subdominios de todos los estudios: ver utils/drive.ts.
 *
 * Permiso pedido: drive.file. Da acceso solo a lo que el abogado marca en el
 * selector; Tabularis nunca ve el resto de su Drive. Los archivos no pasan
 * por nuestro servidor en este paso: van de Google a esta ventana, y de aquí
 * a la pestaña del estudio, que los sube como cualquier adjunto.
 */
import React, { useEffect, useRef, useState } from 'react';
import { GOOGLE_API_KEY, GOOGLE_APP_ID, GOOGLE_CLIENT_ID, driveDisponible, origenPermitido, type MensajeDrive } from '../../utils/drive';

declare global {
  interface Window { google?: any; gapi?: any }
}

const ALCANCE = 'https://www.googleapis.com/auth/drive.file';
const API = 'https://www.googleapis.com/drive/v3';
const CARPETA = 'application/vnd.google-apps.folder';

/** Los Docs, Sheets y Slides no son archivos: se descargan convertidos. Word
 *  y Excel para que se puedan seguir editando; PDF lo que no tiene otro par. */
const EXPORTAR: Record<string, { mime: string; ext: string }> = {
  'application/vnd.google-apps.document': { mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', ext: '.docx' },
  'application/vnd.google-apps.spreadsheet': { mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', ext: '.xlsx' },
  'application/vnd.google-apps.presentation': { mime: 'application/pdf', ext: '.pdf' },
  'application/vnd.google-apps.drawing': { mime: 'application/pdf', ext: '.pdf' },
};

/** Tope de archivos por vez. Todo pasa por la memoria de esta ventana antes
 *  de entregarse; una carpeta más grande se importa por partes, o descargada
 *  desde drive.google.com. */
const MAX_ARCHIVOS = 2000;
const MAX_BYTES = 1.5 * 1024 ** 3;

type Elegido = { id: string; name: string; mimeType: string; size?: string; ruta: string };

const cargarScript = (src: string) =>
  new Promise<void>((ok, mal) => {
    if (document.querySelector(`script[src="${src}"]`)) return ok();
    const s = document.createElement('script');
    s.src = src; s.async = true; s.onload = () => ok(); s.onerror = () => mal(new Error(`No se pudo cargar ${src}`));
    document.head.appendChild(s);
  });

const DriveApp: React.FC = () => {
  const params = new URLSearchParams(window.location.search);
  const origen = params.get('origen') || '';
  const valido = !!window.opener && origenPermitido(origen);

  const [listo, setListo] = useState(false);
  const [estado, setEstado] = useState<'inicio' | 'eligiendo' | 'bajando' | 'error'>('inicio');
  const [mensaje, setMensaje] = useState('');
  const [avance, setAvance] = useState({ hechos: 0, total: 0, nombre: '' });
  const token = useRef<string>('');

  useEffect(() => {
    document.title = 'Google Drive · Tabularis';
    // Sin esto, mientras carga y alrededor del selector se ve el fondo blanco.
    document.body.style.background = '#0d1626';
    document.body.style.margin = '0';
  }, []);

  useEffect(() => {
    if (!valido || !driveDisponible()) return;
    Promise.all([cargarScript('https://accounts.google.com/gsi/client'), cargarScript('https://apis.google.com/js/api.js')])
      .then(() => new Promise<void>(ok => window.gapi.load('picker', () => ok())))
      .then(() => setListo(true))
      .catch(() => { setEstado('error'); setMensaje('No se pudo conectar con Google. Revisa tu conexión y vuelve a intentarlo.'); });
  }, [valido]);

  const enviar = (m: MensajeDrive) => {
    // Solo a la pestaña del estudio que abrió la ventana, nunca a "*".
    window.opener?.postMessage(m, origen);
  };
  const cerrar = (m: MensajeDrive) => { enviar(m); window.close(); };

  const fetchDrive = async (url: string) => {
    const r = await fetch(url, { headers: { Authorization: `Bearer ${token.current}` } });
    if (!r.ok) {
      const cuerpo = await r.json().catch(() => null);
      throw new Error(cuerpo?.error?.message || `Google respondió ${r.status}`);
    }
    return r;
  };

  const descargar = async (elegidos: Elegido[]) => {
    setEstado('bajando');
    try {
      const archivos = elegidos.filter(e => e.mimeType !== CARPETA);
      if (archivos.length > MAX_ARCHIVOS) throw new Error(`Son más de ${MAX_ARCHIVOS.toLocaleString('es-CL')} archivos de una vez. Elige menos.`);
      if (!archivos.length) throw new Error('No elegiste archivos.');
      // Lo que no es un documento (formularios, sitios, accesos directos) no
      // se puede descargar: se deja fuera.
      const bajables = archivos.filter(a => !a.mimeType.startsWith('application/vnd.google-apps.') || EXPORTAR[a.mimeType]);
      const bytes = bajables.reduce((n, a) => n + Number(a.size || 0), 0);
      if (bytes > MAX_BYTES) throw new Error('Son más de 1,5 GB de una vez. Elige menos archivos, o descarga la carpeta desde drive.google.com y arrástrala a Importar documentos.');

      const salida: { ruta: string; tipo: string; blob: Blob }[] = [];
      const fallidos: string[] = [];
      const cuenta = { hechos: 0 };
      setAvance({ hechos: 0, total: bajables.length, nombre: '' });
      const cola = [...bajables];
      const obrero = async () => {
        while (cola.length) {
          const a = cola.shift()!;
          setAvance(v => ({ ...v, nombre: a.name }));
          try {
            const exp = EXPORTAR[a.mimeType];
            const r = exp
              ? await fetchDrive(`${API}/files/${a.id}/export?mimeType=${encodeURIComponent(exp.mime)}`)
              : await fetchDrive(`${API}/files/${a.id}?alt=media&supportsAllDrives=true`);
            const blob = await r.blob();
            const ruta = exp && !a.ruta.toLowerCase().endsWith(exp.ext) ? a.ruta + exp.ext : a.ruta;
            salida.push({ ruta, tipo: exp ? exp.mime : a.mimeType, blob });
          } catch (err: any) {
            fallidos.push(`${a.name}: ${err?.message || 'no se pudo descargar'}`);
          }
          const n = ++cuenta.hechos;
          setAvance(v => ({ ...v, hechos: n }));
        }
      };
      await Promise.all(Array.from({ length: Math.min(4, bajables.length) }, obrero));
      if (!salida.length) throw new Error(`No se pudo descargar ninguno. ${fallidos[0] || ''}`);
      if (fallidos.length) {
        // Se entrega lo que sí bajó y se avisa lo que no, en vez de perderlo todo.
        enviar({ tipo: 'tabularis-drive', estado: 'archivos', archivos: salida });
        setEstado('error');
        setMensaje(`Se entregaron ${salida.length} archivos. Estos no se pudieron descargar:\n${fallidos.slice(0, 10).join('\n')}${fallidos.length > 10 ? `\n…y ${fallidos.length - 10} más` : ''}`);
        return;
      }
      cerrar({ tipo: 'tabularis-drive', estado: 'archivos', archivos: salida });
    } catch (err: any) {
      setEstado('error');
      setMensaje(err?.message || 'No se pudo traer desde Google Drive.');
    }
  };

  const abrirSelector = () => {
    const g = window.google;
    // Las carpetas se ven para poder entrar en ellas, pero no se eligen.
    const vista = new g.picker.DocsView(g.picker.ViewId.DOCS).setIncludeFolders(true).setSelectFolderEnabled(false).setMode(g.picker.DocsViewMode.LIST);
    const compartidas = new g.picker.DocsView(g.picker.ViewId.DOCS).setEnableDrives(true).setIncludeFolders(true).setSelectFolderEnabled(false);
    const selector = new g.picker.PickerBuilder()
      .addView(vista)
      .addView(compartidas)
      .enableFeature(g.picker.Feature.MULTISELECT_ENABLED)
      .enableFeature(g.picker.Feature.SUPPORT_DRIVES)
      .setOAuthToken(token.current)
      .setDeveloperKey(GOOGLE_API_KEY)
      .setAppId(GOOGLE_APP_ID)
      .setLocale('es')
      .setTitle('Elige los archivos (Mayúscula + clic para varios)')
      .setCallback((d: any) => {
        if (d.action === g.picker.Action.PICKED) {
          descargar((d.docs || []).map((x: any) => ({ id: x.id, name: x.name, mimeType: x.mimeType, size: x.sizeBytes, ruta: x.name })));
        } else if (d.action === g.picker.Action.CANCEL) {
          cerrar({ tipo: 'tabularis-drive', estado: 'cancelado' });
        }
      })
      .build();
    selector.setVisible(true);
    setEstado('eligiendo');
  };

  const conectar = () => {
    const g = window.google;
    // Tiene que salir del clic: Google abre su ventana de permiso y los
    // navegadores bloquean las que no vienen de un gesto del usuario.
    const cliente = g.accounts.oauth2.initTokenClient({
      client_id: GOOGLE_CLIENT_ID,
      scope: ALCANCE,
      callback: (r: any) => {
        if (r.error || !r.access_token) {
          setEstado('error');
          setMensaje(r.error === 'access_denied' ? 'No diste permiso para ver los archivos que elijas.' : 'Google no autorizó el acceso. Vuelve a intentarlo.');
          return;
        }
        token.current = r.access_token;
        abrirSelector();
      },
    });
    cliente.requestAccessToken();
  };

  const caja: React.CSSProperties = { maxWidth: 520, margin: '0 auto', padding: '12vh 20px 0', textAlign: 'center', fontFamily: 'Inter, system-ui, sans-serif' };
  const boton: React.CSSProperties = { background: '#BFAF72', color: '#0d1626', border: 'none', borderRadius: 8, padding: '12px 22px', fontSize: 15, fontWeight: 600, cursor: 'pointer' };

  let cuerpo: React.ReactNode;
  if (!valido) {
    cuerpo = <p>Esta ventana se abre desde Tabularis, con el botón "Desde Google Drive" de un caso o de Importar documentos.</p>;
  } else if (!driveDisponible()) {
    cuerpo = <p>La conexión con Google Drive todavía no está habilitada.</p>;
  } else if (estado === 'error') {
    cuerpo = (
      <>
        <p style={{ whiteSpace: 'pre-line', color: '#FFB74D', lineHeight: 1.6 }}>{mensaje}</p>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'center' }}>
          {token.current && <button style={boton} onClick={abrirSelector}>Elegir de nuevo</button>}
          <button style={{ ...boton, background: 'transparent', color: '#D6E4F4', border: '1px solid rgba(255,255,255,0.25)' }} onClick={() => window.close()}>Cerrar</button>
        </div>
      </>
    );
  } else if (estado === 'bajando') {
    cuerpo = (
      <>
        <p style={{ fontSize: 16, color: '#D6E4F4' }}>⏳ Trayendo {avance.total ? `${avance.hechos} de ${avance.total}` : 'los archivos'}…</p>
        <p style={{ fontSize: 12.5, color: '#9BB0CC' }}>{avance.nombre}</p>
        <p style={{ fontSize: 12, color: '#9BB0CC' }}>No cierres esta ventana: se cierra sola al terminar.</p>
      </>
    );
  } else {
    cuerpo = (
      <>
        <p style={{ color: '#D6E4F4', fontSize: 15, lineHeight: 1.6 }}>
          Elige los archivos que quieres agregar al caso.
        </p>
        <p style={{ color: '#9BB0CC', fontSize: 12.5, lineHeight: 1.6 }}>
          Google te pedirá permiso para que Tabularis vea <strong>solo los archivos que elijas</strong>. El resto de tu Drive no se comparte.
        </p>
        <p style={{ color: '#9BB0CC', fontSize: 12.5, lineHeight: 1.6 }}>
          ¿Una carpeta completa? Google no deja traerla desde aquí: descárgala en drive.google.com (clic derecho → Descargar)
          y arrastra el .zip a <strong>Importar documentos</strong>.
        </p>
        <button style={{ ...boton, opacity: listo ? 1 : 0.5 }} disabled={!listo} onClick={conectar}>
          {listo ? 'Elegir de Google Drive' : 'Conectando…'}
        </button>
      </>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#0d1626', color: '#D6E4F4' }}>
      <div style={caja}>
        <div style={{ fontSize: 13, letterSpacing: 2, color: '#BFAF72', marginBottom: 18 }}>TABULARIS · GOOGLE DRIVE</div>
        {cuerpo}
      </div>
    </div>
  );
};

export default DriveApp;
